import type { ADVERBIAL_POSSESSIVE, AnalyticsBase, FindFn, PRIMARY_POSSESSIVE } from "./base"

export function getPossessiveForLesson(
  findFn: FindFn,
  userId: string,
  ownerIds: Array<string>,
): ADVERBIAL_POSSESSIVE {
  return ownerIds ? (ownerIds.includes(userId) ? "owns" : "others") : "unknown"
}

export type AnalyticsLesson =
  | LessonComment
  | LessonCreate
  | LessonDelete
  | LessonEditPaste
  | LessonEditAttachment
  | LessonEditCard
  | LessonEditGoogleClassroom
  | LessonEditStandard
  | LessonEditBasic
  | LessonEditorInteraction
  | LessonEditLessonSlot
  | LessonEditMove
  | LessonAi
  | LessonPrint
  | LessonView
  | LessonCopy

export interface LessonComment extends AnalyticsBase {
  date_plan_scheduled_for: string | null
  primary_object_id: string
  primary_object_id_and_ancestor_ids: Array<string>

  primary_verb: "comment"
  primary_possessive: PRIMARY_POSSESSIVE
  primary_object_type: "lesson"
  primary_object_specifier: "cards" | null

  adverbial_verb: "adding" | "responding" | "removing"
  adverbial_object_type: "comment"
  adverbial_object_specifier: null
  adverbial_object_id: string
  adverbial_object_id_and_ancestor_ids: Array<string>
}

export interface LessonCreate extends AnalyticsBase {
  date_plan_scheduled_for: string | null
  primary_object_id: string
  primary_object_id_and_ancestor_ids: Array<string>

  primary_verb: "create"
  primary_possessive: PRIMARY_POSSESSIVE
  primary_object_type: "lesson"
  primary_object_specifier: null

  adverbial_verb: null | "importing"
  adverbial_object_type: null | "lesson"
  adverbial_object_specifier: null
  adverbial_object_id: string | null
  adverbial_object_id_and_ancestor_ids: null | Array<string>
}

export interface LessonDelete extends AnalyticsBase {
  date_plan_scheduled_for: string | null
  primary_object_id: string
  primary_object_id_and_ancestor_ids: Array<string>

  primary_verb: "delete"
  primary_possessive: PRIMARY_POSSESSIVE
  primary_object_type: "lesson"
  primary_object_specifier: null

  adverbial_verb: "removing"
  adverbial_object_type: "lesson"
  adverbial_object_specifier: "entire_day" | "content" | "entire_object" | null
  adverbial_object_id: null
  adverbial_object_id_and_ancestor_ids: null
}

export interface LessonEditPaste extends AnalyticsBase {
  date_plan_scheduled_for: string | null
  primary_object_id: string
  primary_object_id_and_ancestor_ids: Array<string>

  primary_verb: "edit"
  primary_possessive: PRIMARY_POSSESSIVE
  primary_object_type: "lesson"
  primary_object_specifier: null

  adverbial_verb: "pasting"
  adverbial_object_type: "lesson"
  adverbial_object_specifier: "single_lesson"
  adverbial_object_id: string
  adverbial_possessive: ADVERBIAL_POSSESSIVE
  adverbial_object_id_and_ancestor_ids: Array<string>
}

export interface LessonEditAttachment extends AnalyticsBase {
  date_plan_scheduled_for: string | null
  primary_object_id: string
  primary_object_id_and_ancestor_ids: Array<string>

  primary_verb: "edit"
  primary_possessive: PRIMARY_POSSESSIVE
  primary_object_type: "lesson"
  primary_object_specifier: "attachments"

  adverbial_verb: "adding" | "removing" | "renaming"
  adverbial_object_type: "attachment"
  adverbial_object_specifier:
    | "upload_file"
    | "one_drive_file"
    | "google_drive_file"
    | "dropbox_file"
    | "other"
    | null
  adverbial_object_id: string
  adverbial_possessive: null
  adverbial_object_id_and_ancestor_ids: null | Array<string>
}
export interface LessonEditCard extends AnalyticsBase {
  date_plan_scheduled_for: string | null
  primary_object_id: string
  primary_object_id_and_ancestor_ids: Array<string>

  primary_verb: "edit"
  primary_possessive: PRIMARY_POSSESSIVE
  primary_object_type: "lesson"
  primary_object_specifier: "cards"

  adverbial_verb: null | "adding" | "removing" | "dragging_dropping" | "editing" | "pasting"
  adverbial_object_type: null | "card" | "version" | "standard" | "template"
  adverbial_object_specifier:
    | null
    | "within_lesson"
    | "between_lessons"
    | "public"
    | "value"
    | "color"
    | "title"
    | "value"
    | "lesson"
    | "unit"
  adverbial_object_id: string | null
  adverbial_possessive: ADVERBIAL_POSSESSIVE | null
  adverbial_object_id_and_ancestor_ids: null | Array<string>
}

export interface LessonEditGoogleClassroom extends AnalyticsBase {
  date_plan_scheduled_for: string | null
  primary_object_id: string
  primary_object_id_and_ancestor_ids: Array<string>

  primary_verb: "edit"
  primary_possessive: PRIMARY_POSSESSIVE
  primary_object_type: "lesson"
  primary_object_specifier: "google_classroom"

  adverbial_verb: "editing"
  adverbial_object_type: "card"
  adverbial_object_specifier:
    | "title"
    | "value"
    | "gc_share_mode"
    | "gc_topic"
    | "gc_max_points"
    | "gc_mc_questions"
    | "gc_schedule"
    | "gc_time"
    | "gc_unschedule"
  adverbial_object_id: string
  adverbial_possessive: null
  adverbial_object_id_and_ancestor_ids: null | Array<string>
}

export interface LessonEditStandard extends AnalyticsBase {
  date_plan_scheduled_for: string | null
  primary_object_id: string
  primary_object_id_and_ancestor_ids: Array<string>

  primary_verb: "edit"
  primary_possessive: PRIMARY_POSSESSIVE
  primary_object_type: "lesson"
  primary_object_specifier: "standards"

  adverbial_verb: "adding" | "removing" | "starring"
  adverbial_object_type: "standard"
  adverbial_object_specifier: "lesson" | "unit"
  adverbial_object_id: string
  adverbial_possessive: null
  adverbial_object_id_and_ancestor_ids: null | Array<string>
}

export interface LessonEditBasic extends AnalyticsBase {
  date_plan_scheduled_for: string | null
  primary_object_id: string
  primary_object_id_and_ancestor_ids: Array<string>

  primary_verb: "edit"
  primary_possessive: PRIMARY_POSSESSIVE
  primary_object_type: "lesson"
  primary_object_specifier: "title"

  adverbial_verb: null
  adverbial_object_type: null
  adverbial_object_specifier: null
  adverbial_object_id: null
  adverbial_possessive: null
  adverbial_object_id_and_ancestor_ids: null
}

/**
 * Unified-editor interactions that are not a more specific object edit: formatting/heading/list/
 * color operations, block inserts, comment composer opens, menu opens, paste batches, and drags.
 * These columns are coarse by design — the exact operation, surface, and detail ride in
 * `narrative.context` alongside the row.
 */
export interface LessonEditorInteraction extends AnalyticsBase {
  date_plan_scheduled_for: string | null
  primary_object_id: string
  primary_object_id_and_ancestor_ids: Array<string>

  primary_verb: "edit"
  primary_possessive: PRIMARY_POSSESSIVE
  primary_object_type: "lesson"
  primary_object_specifier: "editor"

  adverbial_verb: null | "editing" | "toggling" | "opening" | "pasting" | "dragging_dropping"
  adverbial_object_type: null | "card" | "lesson"
  adverbial_object_specifier: null
  adverbial_object_id: string | null
  adverbial_possessive: null
  adverbial_object_id_and_ancestor_ids: null | Array<string>
}

export interface LessonEditLessonSlot extends AnalyticsBase {
  date_plan_scheduled_for: string | null
  primary_object_id: string | null
  primary_object_id_and_ancestor_ids: Array<string>

  primary_verb: "edit"
  primary_possessive: PRIMARY_POSSESSIVE
  primary_object_type: "lesson"
  primary_object_specifier: "lesson_slot"

  adverbial_verb: "toggling"
  adverbial_object_type: "lesson" | "template"
  adverbial_object_specifier: "on" | "off" | "off-and-bump" | "off-and-delete"
  adverbial_object_id: string | null
  adverbial_possessive: ADVERBIAL_POSSESSIVE | null
  adverbial_object_id_and_ancestor_ids: null | Array<string>
}

export interface LessonEditMove extends AnalyticsBase {
  date_plan_scheduled_for: string | null
  primary_object_id: string | null
  primary_object_id_and_ancestor_ids: Array<string>

  primary_verb: "move"
  primary_possessive: PRIMARY_POSSESSIVE
  primary_object_type: "lesson"
  primary_object_specifier: null

  adverbial_verb: "bumping" | "dragging_dropping"
  adverbial_object_type: "day" | "lesson"
  adverbial_object_specifier: "backward" | "forward" | "to_different_class" | null
  adverbial_object_id: string | null
  adverbial_possessive: ADVERBIAL_POSSESSIVE | null
  adverbial_object_id_and_ancestor_ids: null | Array<string>
}

/** AI chat interactions; separate from LessonEditAi, which is for applying changes. */
export interface LessonAi extends AnalyticsBase {
  date_plan_scheduled_for: string | null
  primary_object_id: string
  primary_object_id_and_ancestor_ids: Array<string>

  primary_verb: "use"
  primary_possessive: PRIMARY_POSSESSIVE
  primary_object_type: "lesson"
  primary_object_specifier: "ai"

  adverbial_verb: "opening" | "sending" | "responding" | "selecting" | "accepting"
  adverbial_object_type: "chat"
  adverbial_object_specifier:
    | null
    | "message"
    | "accept"
    | "reject"
    | "revise"
    | "suggestion"
    | "rate_limited"
  adverbial_object_id: string
  adverbial_object_id_and_ancestor_ids: Array<string>
}

export interface LessonPrint extends AnalyticsBase {
  date_plan_scheduled_for: string | null
  primary_object_id: string
  primary_object_id_and_ancestor_ids: Array<string>

  primary_verb: "print"
  primary_possessive: PRIMARY_POSSESSIVE
  primary_object_type: "lesson"
  primary_object_specifier: null

  adverbial_verb: null
  adverbial_object_type: "unit" | null
  adverbial_object_specifier: null
  adverbial_object_id: string | null
  adverbial_possessive: null
  adverbial_object_id_and_ancestor_ids: Array<string>
}

export interface LessonView extends AnalyticsBase {
  date_plan_scheduled_for: string | null
  primary_object_id: string
  primary_object_id_and_ancestor_ids: Array<string>

  primary_verb: "view"
  primary_possessive: PRIMARY_POSSESSIVE
  primary_object_type: "lesson"
  primary_object_specifier:
    | "full_version"
    | "student_version"
    | "history"
    | "instructional_hub"
    | "title"
}

export interface LessonCopy extends AnalyticsBase {
  date_plan_scheduled_for: string | null
  primary_object_id: string
  primary_object_id_and_ancestor_ids: Array<string>

  primary_verb: "copy"
  primary_possessive: PRIMARY_POSSESSIVE
  primary_object_type: "lesson"
  primary_object_specifier: null | "cards" | "standards"

  adverbial_verb: null | "copying"
  adverbial_object_type: null | "lesson"
  adverbial_object_specifier: null | "entire_day"
  adverbial_object_id: null | string
  adverbial_possessive: ADVERBIAL_POSSESSIVE | null
  adverbial_object_id_and_ancestor_ids: Array<string>
}

/**
 * Core fields for AnalyticsLesson events.
 * Provided to event's `create` functions so they can expand on it
 * (and sometimes override it).
 */
export interface LessonAnalyticsCore {
  readonly root_id: string | null
  readonly root_type: "planbook"
  readonly date_plan_scheduled_for: string | null
  readonly primary_object_id: string
  readonly primary_object_id_and_ancestor_ids: Array<string>
  readonly primary_possessive: PRIMARY_POSSESSIVE
  readonly primary_verb: "edit"
  readonly primary_object_type: "lesson"
  readonly adverbial_possessive: null
}

export const LessonEditorInteraction = {
  create(
    core: LessonAnalyticsCore,
    fields: {
      readonly verb: LessonEditorInteraction["adverbial_verb"]
      readonly cardId: string | null
    },
  ): LessonEditorInteraction {
    return {
      ...core,
      primary_object_specifier: "editor",
      adverbial_verb: fields.verb,
      adverbial_object_type: fields.cardId === null ? null : "card",
      adverbial_object_specifier: null,
      adverbial_object_id: fields.cardId,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids:
        fields.cardId === null ? null : core.primary_object_id_and_ancestor_ids,
    }
  },
}

export const LessonEditCard = {
  create(
    core: LessonAnalyticsCore,
    fields: {
      readonly verb: LessonEditCard["adverbial_verb"]
      readonly specifier: LessonEditCard["adverbial_object_specifier"]
      readonly cardId: string | null
    },
  ): LessonEditCard {
    return {
      ...core,
      primary_object_specifier: "cards",
      adverbial_verb: fields.verb,
      adverbial_object_type: fields.cardId === null ? null : "card",
      adverbial_object_specifier: fields.specifier,
      adverbial_object_id: fields.cardId,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: core.primary_object_id_and_ancestor_ids,
    }
  },
}

export const LessonEditAttachment = {
  create(
    core: LessonAnalyticsCore,
    fields: {
      readonly verb: LessonEditAttachment["adverbial_verb"]
      readonly specifier: LessonEditAttachment["adverbial_object_specifier"]
      readonly attachmentId: string
    },
  ): LessonEditAttachment {
    return {
      ...core,
      primary_object_specifier: "attachments",
      adverbial_verb: fields.verb,
      adverbial_object_type: "attachment",
      adverbial_object_specifier: fields.specifier,
      adverbial_object_id: fields.attachmentId,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: core.primary_object_id_and_ancestor_ids,
    }
  },
}

export const LessonEditStandard = {
  create(
    core: LessonAnalyticsCore,
    fields: {
      readonly verb: LessonEditStandard["adverbial_verb"]
      readonly standardId: string
    },
  ): LessonEditStandard {
    return {
      ...core,
      primary_object_specifier: "standards",
      adverbial_verb: fields.verb,
      adverbial_object_type: "standard",
      adverbial_object_specifier: "lesson",
      adverbial_object_id: fields.standardId,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: core.primary_object_id_and_ancestor_ids,
    }
  },
}

export const LessonEditGoogleClassroom = {
  create(
    core: LessonAnalyticsCore,
    fields: {
      readonly specifier: LessonEditGoogleClassroom["adverbial_object_specifier"]
      readonly cardId: string
    },
  ): LessonEditGoogleClassroom {
    return {
      ...core,
      primary_object_specifier: "google_classroom",
      adverbial_verb: "editing",
      adverbial_object_type: "card",
      adverbial_object_specifier: fields.specifier,
      adverbial_object_id: fields.cardId,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: core.primary_object_id_and_ancestor_ids,
    }
  },
}

export const LessonCopy = {
  create(
    core: LessonAnalyticsCore,
    fields: {
      readonly specifier: LessonCopy["primary_object_specifier"]
      readonly objectId: string
    },
  ): LessonCopy {
    return {
      ...core,
      primary_verb: "copy",
      primary_object_specifier: fields.specifier,
      adverbial_verb: "copying",
      adverbial_object_type: null,
      adverbial_object_specifier: null,
      adverbial_object_id: fields.objectId,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: core.primary_object_id_and_ancestor_ids,
    }
  },
}

export const LessonEditBasic = {
  create(core: LessonAnalyticsCore): LessonEditBasic {
    return {
      ...core,
      primary_object_specifier: "title",
      adverbial_verb: null,
      adverbial_object_type: null,
      adverbial_object_specifier: null,
      adverbial_object_id: null,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: null,
    }
  },
}
